import { Phone, ShieldCheck, Building2, FileCheck, Users, Wrench, Percent, CalendarClock, Droplets, Check } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/site";
import { Seo } from "@/components/Seo";
import { AssociationForm } from "@/components/AssociationForm";

const FOR_ASSOCIATION = [
  { icon: FileCheck, text: "No contract and no cost to join" },
  { icon: ShieldCheck, text: "Contractor insurance certificates kept current and on file with management, naming the association as additional insured when required" },
  { icon: Building2, text: "Work follows your building's rules: approved hours, elevator reservations, access and noise policies" },
  { icon: Users, text: "One point of contact for the board and management" },
  { icon: Wrench, text: "Free yearly check of common-area AC units, when the building has them" },
  { icon: CalendarClock, text: "Free AC care talk for residents, on request" },
];

const FOR_RESIDENTS = [
  { icon: Percent, text: "10% off repairs" },
  { icon: Check, text: "$30 off the Annual Maintenance Plan ($219/year instead of $249)" },
  { icon: CalendarClock, text: "Priority scheduling" },
  { icon: Droplets, text: "Drain line cleaning at every tune-up, which helps prevent leaks into the units below" },
];

const STEPS = [
  "The board or property manager requests to join",
  "We learn your building's rules and provide insurance certificates for your files",
  "Residents book directly and receive their discounts",
];

const scrollToForm = () => document.getElementById("association-join")?.scrollIntoView({ behavior: "smooth" });

const AssocHero = () => (
  <section className="bg-brand-ink text-white">
    <div className="max-w-7xl mx-auto px-4 py-20 sm:py-28">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-ice mb-3">For Condo Associations &amp; Property Managers</p>
      <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-black max-w-3xl">The Association Partner Program</h1>
      <p className="text-base sm:text-lg text-zinc-300 mt-6 max-w-2xl leading-relaxed">
        A vetted, insured AC contractor your residents can count on, with no contract and no cost to the association.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 mt-8">
        <button onClick={scrollToForm} data-testid="assoc-hero-cta"
          className="bg-brand-ice hover:bg-[#7FA8F0] text-brand-ink font-bold px-8 py-4 rounded-sm transition-colors duration-200">
          Join the Program
        </button>
        <a href={PHONE_HREF} data-testid="assoc-hero-call"
          className="border-2 border-white text-white hover:bg-white hover:text-brand-ink font-bold px-8 py-4 rounded-sm text-center flex items-center justify-center gap-2 transition-colors duration-200">
          <Phone className="w-5 h-5" /> Call {PHONE}
        </a>
      </div>
    </div>
  </section>
);

const BenefitList = ({ title, items, testid }) => (
  <div data-testid={testid} className="bg-white border border-zinc-200 p-8 rounded-sm">
    <h2 className="font-heading font-black text-2xl text-brand-ink">{title}</h2>
    <ul className="mt-5 space-y-4">
      {items.map((b) => (
        <li key={b.text} className="flex items-start gap-3 text-sm text-zinc-700 leading-relaxed">
          <b.icon className="w-5 h-5 text-brand shrink-0 mt-0.5" /> {b.text}
        </li>
      ))}
    </ul>
  </div>
);

const WhyItMatters = () => (
  <section className="bg-brand-ink text-white">
    <div className="max-w-4xl mx-auto px-4 py-16 sm:py-24 text-center">
      <Droplets className="w-10 h-10 text-brand-ice mx-auto mb-4" />
      <h2 className="text-2xl sm:text-3xl tracking-tight font-black">Why it matters</h2>
      <p data-testid="assoc-why-text" className="text-zinc-300 mt-5 leading-relaxed">
        In condo buildings, one clogged AC drain line can leak into the unit below, leading to damage, insurance claims and disputes between neighbors. Regular maintenance through a trusted, insured contractor helps prevent that, and gives residents one reliable number to call.
      </p>
    </div>
  </section>
);

export default function CondoAssociations() {
  return (
    <div data-testid="condo-associations-page">
      <Seo title="Condo Association AC Partner Program | Central Air Depot" description="A vetted AC contractor for your residents at no cost to the association. Insurance on file, building rules followed, resident discounts." />
      <AssocHero />
      <section className="bg-brand-subtle">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <BenefitList title="For the association" items={FOR_ASSOCIATION} testid="assoc-benefits" />
          <BenefitList title="For residents" items={FOR_RESIDENTS} testid="resident-benefits" />
        </div>
      </section>
      <WhyItMatters />
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
          <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink mb-10">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEPS.map((step, i) => (
              <div key={step} data-testid={`assoc-step-${i + 1}`} className="border-l-4 border-brand bg-brand-subtle p-6 rounded-sm">
                <span className="font-heading font-black text-3xl text-brand">{i + 1}</span>
                <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section id="association-join" className="bg-brand-subtle">
        <div className="max-w-3xl mx-auto px-4 py-16 sm:py-24">
          <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink mb-2">Join the Association Partner Program</h2>
          <p className="text-zinc-600 mb-8">Tell us about your building and we'll take it from there.</p>
          <AssociationForm />
        </div>
      </section>
    </div>
  );
}
